import { FaExternalLinkAlt, FaKey, FaTerminal } from 'react-icons/fa';
import { Badge, Button } from '@/components/UI';
import { developerAccessPath, toolLaunchUrls } from './constants';
import { buildMcpSetupCommand, getMcpReadinessBadgeVariant, getScorecardGenerationToolLabel } from './helpers';
import type { McpClientReadiness, ScorecardGenerationExternalTool } from './types';

interface McpReadinessPanelProps {
    readiness: McpClientReadiness | undefined;
    tool: ScorecardGenerationExternalTool;
    onOpenExternalTool: () => void;
}

const McpReadinessPanel = ({ readiness, tool, onOpenExternalTool }: McpReadinessPanelProps) => {
    const setupCommand = readiness?.connector.setupInstructions.join('\n') ?? buildMcpSetupCommand(tool);
    const launchUrl = toolLaunchUrls[tool];
    const status = readiness?.status ?? 'unknown';

    const detail =
        readiness?.detail ??
        `${getScorecardGenerationToolLabel(tool)} setup has not been checked yet. Create a Project314 MCP token in Developer Access if this is your first external-agent run.`;

    return (
        <div className="rounded border border-zinc-800 bg-zinc-900 p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-medium text-zinc-200">
                    <FaKey className="text-zinc-400" />
                    MCP Connection
                </div>
                <Badge variant={getMcpReadinessBadgeVariant(status)}>{readiness?.label ?? 'Checking'}</Badge>
            </div>
            <div className="text-xs leading-5 text-zinc-400">{detail}</div>
            {readiness?.matchedToken && (
                <div className="mt-2 text-xs text-zinc-500">
                    Token {readiness.matchedToken.tokenPrefix} · last used {readiness.matchedToken.lastUsedDateTime ?? 'never'}
                </div>
            )}
            {readiness?.matchedOAuthConnection && (
                <div className="mt-2 text-xs text-zinc-500">
                    OAuth {readiness.matchedOAuthConnection.clientName} · last used{' '}
                    {readiness.matchedOAuthConnection.lastUsedAt ?? 'not yet'}
                </div>
            )}
            {readiness?.connector && (
                <div className="mt-2 text-xs text-zinc-500">
                    Endpoint {readiness.connector.mcpEndpoint}
                    {readiness.connector.authMode === 'oauth_remote_connector' ? ' · OAuth connector path' : ' · bearer token'}
                </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
                <Button icon={<FaKey />} size="sm" to={developerAccessPath} variant="secondary">
                    Developer Access
                </Button>
                {launchUrl && (
                    <Button icon={<FaExternalLinkAlt />} size="sm" variant="secondary" onClick={onOpenExternalTool}>
                        Open {getScorecardGenerationToolLabel(tool)}
                    </Button>
                )}
            </div>
            {setupCommand && status !== 'connected' && (
                <div className="mt-3 rounded border border-zinc-800 bg-black/20 p-2">
                    <div className="mb-1 flex items-center gap-2 text-xs font-medium text-zinc-300">
                        <FaTerminal className="text-zinc-500" />
                        Setup
                    </div>
                    <pre className="whitespace-pre-wrap break-words text-xs leading-5 text-zinc-500">{setupCommand}</pre>
                </div>
            )}
        </div>
    );
};

export default McpReadinessPanel;
